import { useState } from 'react';
import { AvatarPicker } from '../../components/AvatarPicker';
import { ReachIcons } from '../../components/Reach';
import { CountrySelect } from '../../components/CountrySelect';
import { ChannelLogo } from '../../components/ChannelLogo';
import { joinPhone, splitPhone } from './LeadEditor';
import { InsightCards } from '../../components/Insights';
import {
  CardPickerButton,
  applyChoice,
  useCardChoice,
  type CardChoice,
} from '../../components/CardPicker';
import {
  ColumnPickerButton,
  useColumnChoice,
  useColumnDrag,
  type ColumnDef,
} from '../../components/ColumnPicker';
import {
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  useColumnFilters,
  type FilterDef,
} from '../../components/ColumnFilter';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CONTACT_BUCKET_DEFS } from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { useDebounced, useListState } from '../../lib/hooks';
import { plural, relative, WORDS } from '../../lib/format';
import { Icon, type IconName } from '../../components/Icon';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  PageHead,
  Pager,
  SearchBox,
  TableSkeleton,
} from '../../components/ui';
import { DeleteButton } from '../../components/DeleteDialog';
import { Avatar, OwnerChip } from '../../components/People';
import { RecordTimeline } from '../../components/RecordTimeline';
import { Feedback } from '../../components/Feedback';
import { ContactQuickActions } from './ContactQuickActions';

export interface ContactRow {
  id: string;
  accountId: string;
  name: string;
  jobTitle: string | null;
  email: string | null;
  phone: string | null;
  altPhone: string | null;
  /** السوشيال وتوجيه القنوات — كملف العميل المحتمل سواء. */
  messengerHandle?: string | null;
  instagramHandle?: string | null;
  waPhone?: string | null;
  smsPhone?: string | null;
  isPrimary: boolean;
  notes: string | null;
  createdAt: string;
  activityCount: number;
  ownerId: string | null;
  /** الخاص بها أو الموروث من العميل — يحسبه الخادم. */
  effectiveOwnerId: string | null;
  lastContactedAt: string | null;
  nextActionAt: string | null;
  account: { id: string; name: string; kind: string };
  /** الصورة `data:` — تحلّ محلّ حروف الاسم حين توجد. */
  avatarUrl: string | null;
}

interface ContactPage {
  items: ContactRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

/*
 * الأنشطة ما زالت تصل من الخادم ولم تعد تُقرأ هنا: الخطّ الزمني يجلبها
 * بنفسه ومعها ما لا يجلبه هذا المسار — تغيّرات الحقول والمرفقات.
 */
type ContactDetail = ContactRow;

/**
 * جهات الاتصال عبر كل العملاء.
 *
 * ملف العميل يكفي حين تعرف الشركة، ولا يكفي حين تذكر اسم شخص ولا تذكر أين
 * يعمل — وهي الحالة الأشيع بعد شهور من العمل.
 */
/** أيقونة كل دلو — تخصّ هذه الواجهة فلا موضع لها في الكتالوج المشترك. */
const BUCKET_ICONS: Record<string, IconName> = {
  primary: 'check',
  reachable: 'phone',
  noEmail: 'mail',
  unassigned: 'user',
  recent: 'plus',
};

/** ما يُعرض قبل أن يختار المستخدم — الثلاثة التي تُبنى عليها الحملات. */
const DEFAULT_CARDS = ['primary', 'reachable', 'noEmail'];

/** كتالوج أعمدة جهات الاتصال. */
const CONTACT_COLUMNS: readonly ColumnDef[] = [
  { key: 'name', label: 'الاسم', fixed: true },
  { key: 'jobTitle', label: 'المسمى الوظيفي' },
  { key: 'account', label: 'العميل' },
  { key: 'owner', label: 'المسؤول' },
  { key: 'reach', label: 'وسائل التواصل' },
  { key: 'lastContact', label: 'آخر تواصل', group: 'المتابعة' },
  { key: 'nextAction', label: 'النشاط القادم', group: 'المتابعة' },
];

/** أعمدة زرّ «فلتر» — تقابل `CONTACT_FILTERABLE` في accounts.service.ts. */
const CONTACT_FILTERS: FilterDef[] = [
  { key: 'name', label: 'الاسم', kind: 'text', sortable: true },
  { key: 'jobTitle', label: 'المسمى الوظيفي', kind: 'text' },
  { key: 'account', label: 'العميل', kind: 'text', noList: true },
];

const cardChoices: CardChoice[] = CONTACT_BUCKET_DEFS.map((d) => ({
  key: d.key,
  label: d.label,
  group: 'حسب الحالة',
}));

export function ContactsList() {
  const queryClient = useQueryClient();
  const { state, patch, page } = useListState();
  const { filters, setFilters } = useColumnFilters(state, patch);
  const [search, setSearch] = useState(state.q ?? '');
  const debounced = useDebounced(search);
  const [primaryOnly, setPrimaryOnly] = useState(false);
  const [editing, setEditing] = useState<ContactRow | 'new' | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const query = {
    ...state,
    q: debounced || undefined,
    primaryOnly: primaryOnly || undefined,
    page,
  };

  const list = useQuery({
    queryKey: ['contacts', query],
    queryFn: () => api<ContactPage>(`/crm/contacts${qs(query)}`),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['contacts'] });
    setEditing(null);
  };

  /*
   * `bucket` و`page` مستبعدان من استعلام الأعداد: الأول لأن كل بطاقة تعدّ
   * دلوها وحده، والثاني لأن الأعداد للمجموعة كلها لا للصفحة.
   */
  const { bucket: _bucket, page: _page, ...statsQuery } = query as Record<string, unknown>;

  const stats = useQuery({
    queryKey: ['contacts', 'stats', statsQuery],
    queryFn: () => api<Record<string, number>>(`/crm/contacts/stats${qs(statsQuery)}`),
  });

  const { chosen, setChosen, reset: resetCards } = useCardChoice(
    'goyone.cards.contacts',
    DEFAULT_CARDS,
  );

  const {
    visible: shownColumns,
    state: columnState,
    setState: setColumnState,
    reset: resetColumns,
    touched: columnsTouched,
  } = useColumnChoice('goyone.columns.contacts', CONTACT_COLUMNS);
  const colDrag = useColumnDrag(columnState, setColumnState);

  return (
    <div className="page">
      <PageHead
        title="جهات الاتصال"
        subtitle={
          list.data ? `${plural(list.data.total, WORDS.contact)} عبر كل العملاء` : undefined
        }
        /*
          زرّ الإضافة في الشريط اللاصق أسفله — يبقى في المتناول مهما نزل
          المستخدم، كسائر القوائم.
        */
      />

      {/*
        البطاقات تجيب سؤال من يبني حملةً أو يوزّع متابعة، لا سؤال «كم
        عندي»: من بلا بريد لا تصله رسالة، ومن بلا مسؤول لا يتابعه أحد.
      */}
      <InsightCards
        items={applyChoice(
          CONTACT_BUCKET_DEFS.map((d) => ({
            key: d.key,
            label: d.label,
            count: stats.data?.[d.key] ?? 0,
            icon: BUCKET_ICONS[d.key],
            color: d.color,
            hint: d.hint,
          })),
          chosen,
        )}
        active={state.bucket}
        onPick={(next) => patch({ bucket: next, page: undefined })}
        pending={stats.isPending}
        /* الدلاء تتقاطع: الرئيسية قد تكون بلا بريد وبلا مسؤول معًا. */
        showShare={false}
      />

      <div className="toolbar sticky-bar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث بالاسم أو المسمى أو الهاتف"
        />

        <label className="row" style={{ cursor: 'pointer', gap: 6 }}>
          <input
            type="checkbox"
            checked={primaryOnly}
            onChange={(e) => {
              setPrimaryOnly(e.target.checked);
              patch({ page: '1' });
            }}
          />
          <span className="small">الرئيسية فقط</span>
        </label>

        <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
          <FilterButton defs={CONTACT_FILTERS} filters={filters} onChange={setFilters} />
          <ColumnPickerButton
            columns={CONTACT_COLUMNS}
            state={columnState}
            onChange={setColumnState}
            onReset={resetColumns}
            touched={columnsTouched}
          />
          <CardPickerButton
            choices={cardChoices}
            chosen={chosen}
            onChange={setChosen}
            onReset={resetCards}
          />
          <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
            <Icon name="plus" size={14} />
            جهة اتصال
          </button>
        </span>
      </div>

      <FilterChips defs={CONTACT_FILTERS} filters={filters} onChange={setFilters} />

      <FilterScope
        defs={CONTACT_FILTERS}
        filters={filters}
        onChange={setFilters}
        distinctPath="/crm/contacts/distinct"
        query={query}
        sort={state.sort}
        order={state.order}
        onSort={(sort, order) => patch({ sort, order })}
      >
      <div className="card">
        <div className="table-wrap sticky-head">
          <table className="table">
            {/*
              الإجراءات أولًا: الصفحة من اليمين إلى اليسار، فأوّل عمود
              يُرسم في أقصى اليمين تحت الإبهام ومسار العين.
            */}
            <thead>
              <tr>
                <th className="col-act" />
                {shownColumns.map((c) => (
                  <th key={c.key} {...colDrag.thProps(c)}>
                    {c.label}
                    <HeaderFilter colKey={c.key} />
                  </th>
                ))}
              </tr>
            </thead>

            {list.isPending && <TableSkeleton cols={shownColumns.length + 1} />}

            {list.data && (
              <tbody>
                {list.data.items.map((c) => (
                  <tr key={c.id} className="clickable" onClick={() => setOpenId(c.id)}>
                    <td className="col-act" onClick={(e) => e.stopPropagation()}>
                      <ContactQuickActions
                        contact={c}
                        onEdit={() => setEditing(c)}
                        onChanged={() => void list.refetch()}
                      />
                    </td>
                    {shownColumns.map((col) => (
                      <ContactCell key={col.key} column={col} contact={c} />
                    ))}
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}

        {list.data?.items.length === 0 && (
          <EmptyState
            title="لا جهات اتصال"
            text="جهات الاتصال تُضاف من ملف العميل أو من هنا مباشرةً، وتظهر على المستندات وفي سجل التواصل."
            action={
              <button className="btn btn-primary" onClick={() => setEditing('new')}>
                جهة اتصال
              </button>
            }
          />
        )}

        {list.data && (
          <Pager
            page={list.data.page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => patch({ page: String(p) })}
            onPageSize={(n) => patch({ pageSize: String(n), page: undefined })}
          />
        )}
      </div>
      </FilterScope>

      {editing && (
        <ContactModal
          contact={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}

      {openId && (
        <ContactDetailModal
          id={openId}
          onClose={() => setOpenId(null)}
          onEdit={(c) => {
            setOpenId(null);
            setEditing(c);
          }}
        />
      )}
    </div>
  );
}

/* ========================================================================== */

/** خلية عمودٍ واحد — ترتيبها وإظهارها بيد المستخدم من منتقي الأعمدة. */
function ContactCell({ column, contact: c }: { column: ColumnDef; contact: ContactRow }) {
  switch (column.key) {
    case 'name':
      return (
        <td>
          <div className="row" style={{ gap: 8 }}>
            <Avatar name={c.name} src={c.avatarUrl} size={30} />
            <div className="col" style={{ gap: 0 }}>
              <span className="cell-strong">{c.name}</span>
              {c.isPrimary && <span className="tiny mute">جهة الاتصال الرئيسية</span>}
            </div>
          </div>
        </td>
      );
    case 'jobTitle':
      return <td className="cell-mute">{c.jobTitle ?? '—'}</td>;
    case 'account':
      return (
        <td>
          <Link
            to={`/crm/accounts/${c.accountId}`}
            className="link"
            onClick={(e) => e.stopPropagation()}
          >
            {c.account.name}
          </Link>
        </td>
      );
    case 'owner':
      return (
        <td>
          <OwnerChip ownerId={c.effectiveOwnerId} size={22} empty="بلا مسؤول" />
        </td>
      );
    case 'reach':
      /*
        وسائل التواصل أيقونات تعمل لا نصوص تُنسخ يدويًا.
        الغائب منها باهت لا محذوف: غيابه معلومة — جهة بلا بريد
        لا تُرسَل إليها حملة.
      */
      return (
        <td onClick={(e) => e.stopPropagation()}>
          <ReachIcons
            name={c.name}
            phone={c.phone}
            email={c.email}
            link={{ contactId: c.id, accountRefId: c.accountId }}
            waPhone={c.waPhone === 'altPhone' ? c.altPhone : c.phone}
            smsPhone={c.smsPhone === 'altPhone' ? c.altPhone : c.phone}
          />
        </td>
      );
    case 'lastContact':
      return (
        <td className="cell-mute">
          {c.lastContactedAt ? relative(c.lastContactedAt) : 'لم يتم'}
        </td>
      );
    case 'nextAction':
      return (
        <td className="cell-mute">
          {c.nextActionAt ? (
            <span
              style={{
                color:
                  new Date(c.nextActionAt).getTime() < Date.now() ? 'var(--danger)' : undefined,
              }}
            >
              {relative(c.nextActionAt)}
            </span>
          ) : (
            '—'
          )}
        </td>
      );
    default:
      return <td>—</td>;
  }
}

/* ========================================================================== */

/** «@اسم» من مُدخلٍ قد يكون اسمًا أو رابطًا — نصًّا يُقرأ لا رابطًا يلتفّ. */
function socialHandle(raw: string): string {
  return raw
    .replace(/^@/, '')
    .replace(/^https?:\/\/[^/]+\//i, '')
    .replace(/[/?#].*$/, '');
}

function socialHref(kind: 'messenger' | 'instagram', raw: string): string {
  if (/^https?:\/\//i.test(raw)) return raw;
  const handle = encodeURIComponent(socialHandle(raw));
  return kind === 'instagram' ? `https://instagram.com/${handle}` : `https://m.me/${handle}`;
}

export function ContactDetailModal({
  id,
  onClose,
  onEdit,
}: {
  id: string;
  onClose: () => void;
  onEdit: (c: ContactRow) => void;
}) {
  const contact = useQuery({
    queryKey: ['contact', id],
    queryFn: () => api<ContactDetail>(`/crm/contacts/${id}`),
  });

  const c = contact.data;

  return (
    <Modal
      /* الاسم في صدر البروفايل بصورته — فعنوان النافذة صفةُ ما يُعرض. */
      title="ملف جهة الاتصال"
      onClose={onClose}
      wide
      footer={
        <>
          {c && (
            <button className="btn me-auto" onClick={() => onEdit(c)}>
              <Icon name="edit" size={14} />
              تعديل
            </button>
          )}
          <button className="btn btn-primary" onClick={onClose}>
            إغلاق
          </button>
        </>
      }
    >
      {contact.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {c && (
        <>
          {/*
            صدر البروفايل: من هو، وصفته، وعند أيّ عميل — ووسائل بلوغه
            أزرارًا تعمل. وتحته بطاقةٌ بصفوفٍ أيقونية كلوحة بيانات العميل
            سواء، فالنظام كلّه لغةٌ واحدة لا شاشةٌ بلهجتها.
          */}
          <div className="ct-head">
            <Avatar name={c.name} src={c.avatarUrl} size={54} />
            <div className="ct-id">
              <h3 className="ct-name">{c.name}</h3>
              <span className="tiny mute">{c.jobTitle || 'بلا مسمًّى وظيفي'}</span>
              <span className="row" style={{ gap: 6, alignItems: 'center' }}>
                <Icon name="building" size={12} />
                <Link to={`/crm/accounts/${c.accountId}`} className="link small">
                  {c.account.name}
                </Link>
                {c.isPrimary && <Badge kind="brand">جهة رئيسية</Badge>}
              </span>
            </div>
            <span className="ct-reach">
              <ReachIcons
                name={c.name}
                phone={c.phone}
                email={c.email}
                link={{ contactId: c.id, accountRefId: c.accountId }}
                waPhone={c.waPhone === 'altPhone' ? c.altPhone : c.phone}
                smsPhone={c.smsPhone === 'altPhone' ? c.altPhone : c.phone}
                size={16}
              />
            </span>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <div className="ld-rows">
                {/* الرقمان بوسمَي التوجيه — يُقرأ أيُّهما يستقبل واتساب وأيُّهما SMS. */}
                <div className="ld-row">
                  <span className="ld-ico">
                    <Icon name="phone" size={14} />
                  </span>
                  <span className="ld-label">الهاتف</span>
                  <span className="ld-value num">
                    {c.phone ? (
                      <a className="link" dir="ltr" href={`tel:${c.phone}`}>
                        {c.phone}
                      </a>
                    ) : (
                      <span className="mute">—</span>
                    )}
                  </span>
                  {c.phone &&
                    ((c.waPhone ?? 'phone') === 'phone' || (c.smsPhone ?? 'phone') === 'phone') && (
                      <span className="ld-tags">
                        {(c.waPhone ?? 'phone') === 'phone' && (
                          <span className="ld-tag is-wa">واتساب</span>
                        )}
                        {(c.smsPhone ?? 'phone') === 'phone' && <span className="ld-tag">SMS</span>}
                      </span>
                    )}
                </div>

                {c.altPhone && (
                  <div className="ld-row">
                    <span className="ld-ico">
                      <Icon name="phone" size={14} />
                    </span>
                    <span className="ld-label">هاتف آخر</span>
                    <span className="ld-value num">
                      <a className="link" dir="ltr" href={`tel:${c.altPhone}`}>
                        {c.altPhone}
                      </a>
                    </span>
                    {(c.waPhone === 'altPhone' || c.smsPhone === 'altPhone') && (
                      <span className="ld-tags">
                        {c.waPhone === 'altPhone' && <span className="ld-tag is-wa">واتساب</span>}
                        {c.smsPhone === 'altPhone' && <span className="ld-tag">SMS</span>}
                      </span>
                    )}
                  </div>
                )}

                <div className="ld-row">
                  <span className="ld-ico">
                    <Icon name="mail" size={14} />
                  </span>
                  <span className="ld-label">البريد</span>
                  <span className="ld-value num">
                    {c.email ? (
                      <a className="link" dir="ltr" href={`mailto:${c.email}`}>
                        {c.email}
                      </a>
                    ) : (
                      <span className="mute">—</span>
                    )}
                  </span>
                </div>

                {/* السوشيال «@اسم» بأيقونته يفتح الحساب — لا رابطًا يلتفّ. */}
                {c.messengerHandle && (
                  <div className="ld-row">
                    <span className="ld-ico">
                      <ChannelLogo kind="MESSENGER" size={14} />
                    </span>
                    <span className="ld-label">ماسنجر</span>
                    <span className="ld-value num">
                      <a
                        className="link"
                        dir="ltr"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={socialHref('messenger', c.messengerHandle)}
                      >
                        @{socialHandle(c.messengerHandle)}
                      </a>
                    </span>
                  </div>
                )}

                {c.instagramHandle && (
                  <div className="ld-row">
                    <span className="ld-ico">
                      <ChannelLogo kind="INSTAGRAM" size={14} />
                    </span>
                    <span className="ld-label">إنستغرام</span>
                    <span className="ld-value num">
                      <a
                        className="link"
                        dir="ltr"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={socialHref('instagram', c.instagramHandle)}
                      >
                        @{socialHandle(c.instagramHandle)}
                      </a>
                    </span>
                  </div>
                )}

                {c.notes && (
                  <div className="ld-row is-note">
                    <span className="ld-ico">
                      <Icon name="note" size={14} />
                    </span>
                    <span className="ld-label">ملاحظات</span>
                    <span className="ld-value">{c.notes}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/*
            الخطّ الزمني بدل جدول الأنشطة.
            الجدول كان يعرض الأنشطة وحدها، فتغيّر المسؤول والمرفقات يقعان
            خارجه. والخادم يدعم `contact` منذ بناء الخطّ — كان ينقص وصله هنا.
          */}
          <div className="mt-4">
            <RecordTimeline entity="contact" id={c.id} title="سجل التواصل" />
          </div>

          <div className="mt-4">
            <Feedback entity="contact" entityId={c.id} title="ملاحظات الفريق" />
          </div>
        </>
      )}
    </Modal>
  );
}

/* ========================================================================== */

export function ContactModal({
  contact,
  onClose,
  onSaved,
}: {
  contact: ContactRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    accountId: contact?.accountId ?? '',
    name: contact?.name ?? '',
    jobTitle: contact?.jobTitle ?? '',
    email: contact?.email ?? '',
    messengerHandle: contact?.messengerHandle ?? '',
    instagramHandle: contact?.instagramHandle ?? '',
    isPrimary: contact?.isPrimary ?? false,
    notes: contact?.notes ?? '',
    avatarUrl: contact?.avatarUrl ?? '',
  });

  /* الهاتفان مفكوكين بمفتاح دولةٍ وتوجيه قنوات — كملف العميل المحتمل. */
  const [phones, setPhones] = useState(() => ({
    phone: splitPhone(contact?.phone ?? ''),
    altPhone: splitPhone(contact?.altPhone ?? ''),
  }));
  const [routing, setRouting] = useState<{ wa: 'phone' | 'altPhone'; sms: 'phone' | 'altPhone' }>({
    wa: contact?.waPhone === 'altPhone' ? 'altPhone' : 'phone',
    sms: contact?.smsPhone === 'altPhone' ? 'altPhone' : 'phone',
  });

  const set = (key: string, value: unknown) => setForm((f) => ({ ...f, [key]: value }));

  const accounts = useQuery({
    queryKey: ['accounts', 'options'],
    queryFn: () =>
      api<{ items: Array<{ id: string; name: string }> }>(
        '/crm/accounts?pageSize=100&sort=name&order=asc',
      ),
  });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name,
        jobTitle: form.jobTitle || undefined,
        email: form.email || undefined,
        phone: joinPhone(phones.phone.code, phones.phone.local) || undefined,
        altPhone: joinPhone(phones.altPhone.code, phones.altPhone.local) || undefined,
        messengerHandle: form.messengerHandle || undefined,
        instagramHandle: form.instagramHandle || undefined,
        waPhone: routing.wa,
        smsPhone: routing.sms,
        isPrimary: form.isPrimary,
        notes: form.notes || undefined,
        /* null لا undefined: الحذف فعلٌ يجب أن يصل، وundefined تعني «اتركه». */
        avatarUrl: form.avatarUrl || null,
      };
      return contact
        ? api(`/crm/contacts/${contact.id}`, { method: 'PATCH', body })
        : api('/crm/contacts', { method: 'POST', body: { ...body, accountId: form.accountId } });
    },
    onSuccess: onSaved,
  });

  const valid = form.name.trim().length >= 2 && (contact || form.accountId);

  return (
    <Modal
      title={contact ? `تعديل ${contact.name}` : 'جهة اتصال جديدة'}
      onClose={onClose}
      footer={
        <>
          {/*
            الحذف يمرّ على نافذة الفحص كبقية الأنواع.
            كان زرًّا يحذف بضغطة واحدة بلا تأكيد ولا عرض لما يقع، وبلا
            إظهار خطأ إن رفض الخادم — فيبقى النموذج مفتوحًا ويظن من ضغط
            أن شيئًا لم يحدث.
          */}
          {contact && (
            <DeleteButton
              entity="contact"
              id={contact.id}
              endpoint={`/crm/contacts/${contact.id}`}
              className="btn btn-danger me-auto"
              onDeleted={onSaved}
            />
          )}
          <button className="btn" onClick={onClose} type="button">
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      <AvatarPicker
        name={form.name}
        value={form.avatarUrl}
        email={form.email}
        onChange={(next) => set('avatarUrl', next ?? '')}
      />

      <div className="form-grid">
        {!contact && (
          <div className="field span-2">
            <label className="label req" htmlFor="ct-account">
              العميل
            </label>
            <select
              id="ct-account"
              className="select"
              value={form.accountId}
              onChange={(e) => set('accountId', e.target.value)}
            >
              <option value="">اختر عميلًا</option>
              {accounts.data?.items.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <span className="tiny mute">لا تُنقل جهة الاتصال بين العملاء بعد إنشائها.</span>
          </div>
        )}

        <div className="field span-2">
          <label className="label req" htmlFor="ct-name">
            الاسم
          </label>
          <input
            id="ct-name"
            className="input"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            autoFocus
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="ct-title">
            المسمى الوظيفي
          </label>
          <input
            id="ct-title"
            className="input"
            value={form.jobTitle}
            onChange={(e) => set('jobTitle', e.target.value)}
            placeholder="مدير التسويق"
          />
        </div>

        {/* الهاتفان بمفتاح دولةٍ وصناديق التوجيه — كملف العميل المحتمل سواء. */}
        {(
          [
            ['phone', 'الهاتف'],
            ['altPhone', 'هاتف بديل'],
          ] as Array<['phone' | 'altPhone', string]>
        ).map(([key, label]) => (
          <div key={key} className="field">
            <label className="label" htmlFor={`ct-${key}`}>
              {label}
            </label>
            <div className="row" style={{ gap: 6 }}>
              <CountrySelect
                value={phones[key].code}
                onChange={(code) => setPhones((ph) => ({ ...ph, [key]: { ...ph[key], code } }))}
                ariaLabel={`مفتاح دولة ${label}`}
              />
              <input
                id={`ct-${key}`}
                className="input num"
                dir="ltr"
                inputMode="tel"
                value={phones[key].local}
                onChange={(e) =>
                  setPhones((ph) => ({ ...ph, [key]: { ...ph[key], local: e.target.value } }))
                }
                placeholder="10 1234 5678"
              />
            </div>
            <span className="row tiny" style={{ gap: 'var(--sp-3)' }}>
              <label className="row" style={{ cursor: 'pointer', gap: 4 }}>
                <input
                  type="checkbox"
                  checked={routing.wa === key}
                  onChange={() => setRouting((r) => ({ ...r, wa: key }))}
                />
                واتساب
              </label>
              <label className="row" style={{ cursor: 'pointer', gap: 4 }}>
                <input
                  type="checkbox"
                  checked={routing.sms === key}
                  onChange={() => setRouting((r) => ({ ...r, sms: key }))}
                />
                SMS
              </label>
            </span>
          </div>
        ))}

        <div className="field">
          <label className="label" htmlFor="ct-msgr">
            حساب ماسنجر
          </label>
          <input
            id="ct-msgr"
            className="input num"
            dir="ltr"
            value={form.messengerHandle}
            onChange={(e) => set('messengerHandle', e.target.value)}
            placeholder="username أو رابط m.me"
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="ct-ig">
            حساب إنستغرام
          </label>
          <input
            id="ct-ig"
            className="input num"
            dir="ltr"
            value={form.instagramHandle}
            onChange={(e) => set('instagramHandle', e.target.value)}
            placeholder="username أو رابط الحساب"
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="ct-email">
            البريد الإلكتروني
          </label>
          <input
            id="ct-email"
            className="input num"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            dir="ltr"
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="ct-notes">
            ملاحظات
          </label>
          <textarea
            id="ct-notes"
            className="textarea"
            rows={2}
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
          />
        </div>
      </div>

      <label className="row mt-4" style={{ cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={form.isPrimary}
          onChange={(e) => set('isPrimary', e.target.checked)}
        />
        <span className="small">جهة الاتصال الرئيسية — تظهر على مستندات هذا العميل</span>
      </label>
    </Modal>
  );
}

/* ==========================================================================
   وسائل التواصل
   ========================================================================== */

